/**
 * TMDB API layer with IndexedDB caching.
 * Preserved from v1 with caching added.
 */

const API_KEY = '2fd93a6ae014f8a40789924655c0c76d';
const API = 'https://api.themoviedb.org/3';
const IMG = 'https://image.tmdb.org/t/p';

const CACHE_DB_NAME = 'cts-tmdb-cache';
const CACHE_DB_VERSION = 1;
const CACHE_STORE = 'responses';
const CACHE_TTL = 7 * 24 * 60 * 60 * 1000; // 7 days

let cacheDb = null;

async function openCacheDb() {
  if (cacheDb) return cacheDb;
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(CACHE_DB_NAME, CACHE_DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(CACHE_STORE)) {
        db.createObjectStore(CACHE_STORE, { keyPath: 'url' });
      }
    };
    req.onsuccess = () => { cacheDb = req.result; resolve(cacheDb); };
    req.onerror = () => resolve(null);
  });
}

async function getCached(url) {
  try {
    const db = await openCacheDb();
    if (!db) return null;
    return new Promise((resolve) => {
      const tx = db.transaction(CACHE_STORE, 'readonly');
      const store = tx.objectStore(CACHE_STORE);
      const req = store.get(url);
      req.onsuccess = () => {
        const entry = req.result;
        if (entry && Date.now() - entry.timestamp < CACHE_TTL) {
          resolve(entry.data);
        } else {
          resolve(null);
        }
      };
      req.onerror = () => resolve(null);
    });
  } catch {
    return null;
  }
}

async function setCache(url, data) {
  try {
    const db = await openCacheDb();
    if (!db) return;
    const tx = db.transaction(CACHE_STORE, 'readwrite');
    const store = tx.objectStore(CACHE_STORE);
    store.put({ url, data, timestamp: Date.now() });
  } catch {
    // Cache write failures are non-critical
  }
}

export function imgUrl(path, size) {
  if (!path) return '';
  const sizes = {
    sm: 'w150_and_h225_bestv2',
    md: 'w300_and_h450_bestv2',
    lg: 'w600_and_h900_bestv2',
  };
  return `${IMG}/${sizes[size] || sizes.sm}${path}`;
}

function getBrowserLanguage() {
  try {
    return localStorage.getItem('userLanguage') || navigator.language || 'en-US';
  } catch {
    return 'en-US';
  }
}

export async function tmdb(endpoint, params = {}) {
  const url = new URL(`${API}${endpoint}`);
  url.searchParams.set('api_key', API_KEY);
  url.searchParams.set('language', getBrowserLanguage());
  for (const [k, v] of Object.entries(params)) url.searchParams.set(k, v);

  const urlStr = url.toString();

  // Check cache first
  const cached = await getCached(urlStr);
  if (cached) return cached;

  const r = await fetch(url);
  if (!r.ok) throw new Error(`TMDB ${r.status}`);
  const data = await r.json();

  // Cache the response
  await setCache(urlStr, data);

  return data;
}

export async function searchTmdb(query, type = 'multi') {
  if (!query || query.length < 2) return [];
  const endpoint = type === 'multi' ? '/search/multi' : `/search/${type}`;
  const data = await tmdb(endpoint, { query, include_adult: 'false' });
  return data.results || [];
}

export async function searchPerson(query) {
  if (!query || query.length < 2) return [];
  const data = await tmdb('/search/person', { query, include_adult: 'false' });
  return data.results || [];
}

export async function getTrending(mediaType = 'person', timeWindow = 'week') {
  const data = await tmdb(`/trending/${mediaType}/${timeWindow}`);
  return data.results || [];
}
